import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { NeuralChatAuthProvider, useNeuralChatAuth } from "./contexts/AuthContext";
import { CenterState, StateCard } from "./styles";
import { NeuralChatShell } from "./components/NeuralChatShell";
import { NeuralChatHomePage } from "./pages/NeuralChatHomePage";
import { NeuralChatLoginPage } from "./pages/NeuralChatLoginPage";
import { NeuralChatRoomPage } from "./pages/NeuralChatRoomPage";
import {
  ApprovalDetailPage,
  ApprovalInboxPage,
  OpportunityDetailPage,
  OpportunityListPage,
  ProjectDetailPage,
  ProjectListPage,
} from "./pages/NeuralChatProjectWorkspace";
import { getSafeNeuralChatPath } from "./utils/routes";
import { MailDraftDetailPage, MailDraftsPage, MailInboxPage, MailRoutingPage, MailSettingsPage, MailThreadPage } from "./pages/NeuralChatMailWorkspace";
import { ActionCenterPage, DailyReportsPage, MeetingDetailPage, MeetingListPage, WorkflowDetailPage, WorkflowListPage } from "./pages/NeuralChatCollaborationPages";
import { DeliveryCandidatePage, DeliveryCenterPage } from "./pages/NeuralChatDeliveryPages";
import { MailOutboxDetailPage, MailOutboxPage } from "./pages/NeuralChatMailOutbox";
import { SalesCockpitPage, SalesEngagementPage, SalesFollowUpsPage, SalesResponseQueuePage } from "./pages/NeuralChatSalesPages";

const ProtectedLayout: React.FC = () => {
  const auth = useNeuralChatAuth();
  const location = useLocation();
  if (auth.status === "initializing") return <CenterState><StateCard><h1>NeuralChat</h1><p>인증 상태를 확인하는 중…</p></StateCard></CenterState>;
  if (auth.status !== "authenticated") return <Navigate to={`/neuralchat/login?next=${encodeURIComponent(getSafeNeuralChatPath(location.pathname + location.search))}`} replace />;
  return <NeuralChatShell />;
};

export const NeuralChatRoutes: React.FC = () => (
  <NeuralChatAuthProvider>
    <Routes>
      <Route path="login" element={<NeuralChatLoginPage />} />
      <Route element={<ProtectedLayout />}>
        <Route index element={<NeuralChatHomePage />} />
        <Route path="rooms/:roomId" element={<NeuralChatRoomPage />} />
        <Route path="opportunities" element={<OpportunityListPage />} />
        <Route path="opportunities/:opportunityId" element={<OpportunityDetailPage />} />
        <Route path="projects" element={<ProjectListPage />} />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="projects/:projectId/revisions/:revision" element={<ProjectDetailPage />} />
        <Route path="approvals" element={<ApprovalInboxPage />} />
        <Route path="approvals/:approvalId" element={<ApprovalDetailPage />} />
        <Route path="mail" element={<MailInboxPage />} />
        <Route path="mail/threads/:threadId" element={<MailThreadPage />} />
        <Route path="mail/drafts" element={<MailDraftsPage />} />
        <Route path="mail/drafts/:draftId" element={<MailDraftDetailPage />} />
        <Route path="mail/routing" element={<MailRoutingPage />} />
        <Route path="mail/settings" element={<MailSettingsPage />} />
        <Route path="mail/outbox" element={<MailOutboxPage />} />
        <Route path="mail/outbox/:intentId" element={<MailOutboxDetailPage />} />
        <Route path="workflows" element={<WorkflowListPage />} />
        <Route path="workflows/:workflowId" element={<WorkflowDetailPage />} />
        <Route path="meetings" element={<MeetingListPage />} />
        <Route path="meetings/:meetingId" element={<MeetingDetailPage />} />
        <Route path="reports" element={<DailyReportsPage />} />
        <Route path="action-center" element={<ActionCenterPage />} />
        <Route path="delivery" element={<DeliveryCenterPage />} />
        <Route path="delivery/:candidateId" element={<DeliveryCandidatePage />} />
        <Route path="sales" element={<SalesCockpitPage />} />
        <Route path="sales/:engagementId" element={<SalesEngagementPage />} />
        <Route path="sales/responses" element={<SalesResponseQueuePage />} />
        <Route path="sales/follow-ups" element={<SalesFollowUpsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/neuralchat" replace />} />
    </Routes>
  </NeuralChatAuthProvider>
);
